import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { useNavigate } from 'react-router-dom'
import {
  AGENDA_DATE_END,
  AGENDA_DATE_START,
  AGENDA_TASK_ASSIGNMENT,
  AGENDA_TASK_CREATION,
  AGENDA_TASK_PUBLICATION,
} from 'lib/agenda'
import { TABLE_ROWS_PER_PAGE_OPTIONS } from 'lib/tableLib'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'

import Table from 'components/atoms/Table'
import AgendaStateCell from 'components/organisms/AgendaTable/AgendaStateCell'
import SearchField from 'components/atoms/SearchField'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import FilterSelect from 'components/atoms/FilterSelect'
import FilterDateRange from 'components/atoms/FilterDateRange'

import useTablePagination from 'hooks/useTablePagination'

import useAgendaData from './useAgendaData'
import useAgendaFilters from './useAgendaFilters'

const createColumns = (t, agendaFilters) => {
  const {
    filters,
    filtersData,
    setSessionIdQuery,
    setTasks,
    setStates,
    setPublicationRange,
    setStartDateRange,
    setEndDateRange,
  } = agendaFilters

  const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

  return [
    {
      accessor: 'sessionId',
      Header: (
        <TableHeaderCell
          label={t('agenda.table.sessionId')}
          filter={
            <SearchField
              placeholder={t('common.form.searchSessionId')}
              value={filters.sessionIdQuery}
              onChange={(event) => setSessionIdQuery(event.target.value)}
              fullWidth={false}
              sx={{ width: (theme) => theme.spacing(15) }}
              data-testid="filter-sessionId"
            />
          }
        />
      ),
    },
    {
      accessor: 'publicationDate',
      Header: (
        <TableHeaderCell
          label={t('agenda.table.publication')}
          filter={
            <FilterDateRange
              value={[filters.publicationFrom, filters.publicationTo]}
              handleChange={setPublicationRange}
              data-testid="filter-publication"
            />
          }
        />
      ),
    },
    {
      accessor: 'task',
      Header: (
        <TableHeaderCell
          label={t('agenda.table.task')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.tasks}
              value={filters.tasks}
              handleChange={setTasks}
              multiple
              data-testid="filter-task"
            />
          }
        />
      ),
      Cell: ({ value }) => t(`agenda.task.${value}`),
    },
    {
      accessor: AGENDA_DATE_START,
      Header: (
        <TableHeaderCell
          label={t('agenda.table.startDate')}
          filter={
            <FilterDateRange
              value={[filters.startDateFrom, filters.startDateTo]}
              handleChange={setStartDateRange}
              data-testid="filter-startDate"
            />
          }
        />
      ),
    },
    {
      accessor: AGENDA_DATE_END,
      Header: (
        <TableHeaderCell
          label={t('agenda.table.endDate')}
          filter={
            <FilterDateRange
              value={[filters.endDateFrom, filters.endDateTo]}
              handleChange={setEndDateRange}
              data-testid="filter-endDate"
            />
          }
        />
      ),
    },
    {
      accessor: 'state',
      Header: (
        <TableHeaderCell
          label={t('agenda.table.status')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.states}
              value={filters.states}
              handleChange={setStates}
              multiple
              data-testid="filter-status"
            />
          }
        />
      ),
    },
  ]
}

export default function AgendaTable() {
  const { t } = useTranslation()

  const agendaFilters = useAgendaFilters()

  const { filters, setSort, initFilters } = agendaFilters

  const columns = useMemo(
    () => createColumns(t, agendaFilters),
    [agendaFilters, t]
  )

  const {
    page: newPage,
    rowsPerPage: newRowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTablePagination()

  const { items, isFetching, total, isFetchingOtherPages, page, rowsPerPage } =
    useAgendaData({
      newPage,
      limit: newRowsPerPage,
      filters,
    })

  useEffect(() => {
    if (!items.length || filters?.initialized) {
      return
    }

    initFilters(items)
  }, [items, initFilters, filters?.initialized])

  const navigate = useNavigate()

  const preparedItems =
    items?.map((item) => {
      return {
        ...item,
        publicationDate: format(
          new Date(item.publicationDate),
          t('common.date.formatDMY'),
          {
            locale: itLocale,
          }
        ),
        startsAt: format(new Date(item.startsAt), t('common.date.format'), {
          locale: itLocale,
        }),
        endsAt: format(new Date(item.endsAt), t('common.date.format'), {
          locale: itLocale,
        }),
        state: <AgendaStateCell state={item.state} />,
      }
    }) || []

  const handleRowClick = (row) => {
    const pathToRedirect =
      row.values.task === AGENDA_TASK_CREATION
        ? `/sessions/${row.values.sessionId}`
        : row.values.task === AGENDA_TASK_PUBLICATION
        ? `/cars-to-buy`
        : row.values.task === AGENDA_TASK_ASSIGNMENT
        ? `/assignment/${row.values.sessionId}`
        : '#'
    navigate(pathToRedirect)
  }

  return (
    <Table
      isFetching={isFetching}
      isFetchingOtherPages={isFetchingOtherPages}
      items={preparedItems}
      columns={columns}
      total={total}
      getRowProps={(row) => ({
        onClick: (e) => {
          handleRowClick(row)
        },
        sx: { cursor: 'pointer' },
      })}
      hasPagination={true}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      sortable
      orderColumn={filters?.order?.column}
      orderDirection={filters?.order?.direction}
      handleSort={setSort}
    />
  )
}
